import { RmnSettings, ResolvedRmnPalette } from '../types/rmn'

// TDC defaults — match the tool pages' existing visual language
// (ProposalFlow/theme.ts GOLD family) so an RMN with no settings renders
// like the standard tools.
export const TDC_TOOL_DEFAULTS = {
  background: '#FAFAF8',
  primary: '#C8A97E',
  secondary: '#224d89',
}

const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)))

export const hexToRgb = (hex: string): [number, number, number] | null => {
  let h = (hex || '').trim().replace(/^#/, '')
  if (h.length === 3 || h.length === 4) {
    h = h
      .slice(0, 3)
      .split('')
      .map((c) => c + c)
      .join('')
  }
  if (h.length === 8) h = h.slice(0, 6)
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ]
}

const rgbToHex = (r: number, g: number, b: number) =>
  `#${[r, g, b].map((v) => clamp(v).toString(16).padStart(2, '0')).join('')}`

// Linear mix of two hex colors; t=0 → a, t=1 → b.
export const mixHex = (a: string, b: string, t: number): string => {
  const ca = hexToRgb(a)
  const cb = hexToRgb(b)
  if (!ca || !cb) return a
  return rgbToHex(
    ca[0] + (cb[0] - ca[0]) * t,
    ca[1] + (cb[1] - ca[1]) * t,
    ca[2] + (cb[2] - ca[2]) * t
  )
}

// Black or white text, whichever contrasts better (WCAG relative luminance).
export const contrastText = (hex: string): string => {
  const rgb = hexToRgb(hex)
  if (!rgb) return '#000000'
  const [r, g, b] = rgb.map((v) => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  })
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b
  return luminance > 0.4 ? '#000000' : '#ffffff'
}

// Resolve rmn_settings (+ defaults) into the palette consumed by
// RmnThemeProvider and the admin live preview.
export const resolveRmnPalette = (
  settings?: RmnSettings | null,
  brandName?: string
): ResolvedRmnPalette => {
  const s = settings || {}
  const primary = s.primary_color || TDC_TOOL_DEFAULTS.primary
  return {
    background: s.bg_color || TDC_TOOL_DEFAULTS.background,
    primary,
    primaryDark: mixHex(primary, '#000000', 0.25),
    primaryTint: mixHex(primary, '#ffffff', 0.85),
    secondary: s.secondary_color || TDC_TOOL_DEFAULTS.secondary,
    systemName:
      s.system_name || (brandName ? `${brandName} Commerce Content OS` : 'Commerce Content OS'),
    loginTagline: s.login_tagline,
  }
}
